import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';

import SalesReportHeader from './SalesReportHeader';
import './SalesReport.scss';

// Dummy restaurants — replace with API/Redux data
const RESTAURANTS = [
    { label: 'Chenthoor Restaurant', value: 'Chenthoor Restaurant' },
    { label: 'Chenthoor Restaurant - 2', value: 'Chenthoor Restaurant - 2' },
];

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const pad = (n) => String(n).padStart(2, '0');

// dd.mm.yy, same as the reference sheet
const formatDate = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;

const formatNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function generateDateRange(from, to) {
    const dates = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (cur <= end) {
        dates.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

// Dummy daily sales — replace with API data: { date, totalSales, billFrom, billTo, gpay, cancelled }
// Cash = Total Sales - GPay - Cancelled/Dis/Adv Bill, as in the reference sheet.
function buildRows(dates) {
    let billTo = 0;
    return dates.map((date, i) => {
        const seed = date.getDate() * 37 + (date.getMonth() + 1) * 11 + i * 7;
        const billCount = 15 + (seed % 45);
        const totalSales = 3000 + ((seed * 913) % 30000);
        const gpay = round2(totalSales * (0.3 + (seed % 30) / 100));
        const cancelled = seed % 9 === 0 ? 100 + (seed % 5) * 50 : 0;
        const row = {
            id: i,
            date,
            totalSales,
            billFrom: billTo + 1,
            billTo: billTo + billCount,
            gpay,
            cancelled,
            cash: round2(totalSales - gpay - cancelled),
        };
        billTo = row.billTo;
        return row;
    });
}

const COLUMN_LABELS = ['DATE', 'TOTAL SALES', 'BILL FROM', 'BILL TO', 'GPAY', 'CANCELLED/DIS/ADV BILL', 'CASH'];

const defaultRange = () => {
    const now = new Date();
    return { fromDate: new Date(now.getFullYear(), now.getMonth(), 1), toDate: now };
};



class SalesReport extends Component {
    state = {
        Restaurant: RESTAURANTS[0].value,
        ...defaultRange(),
        tableData: [],
        searched: false,
        btnLoading: null, // key of the action currently running: search | showAll | clear | pdf | excel
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    showError = (detail) => this.growl && this.growl.show({ severity: 'error', summary: 'Error', detail });

    // Full-page .page-loader overlay while the action runs; further clicks ignored meanwhile.
    runWithLoader = (key, action) => {
        if (this.state.btnLoading) return;
        this.setState({ btnLoading: key }, () => {
            setTimeout(() => {
                try {
                    action();
                } catch (e) {
                    this.showError(key === 'pdf' || key === 'excel' ? 'Export failed. Please try again.' : 'Something went wrong. Please try again.');
                } finally {
                    if (!this.unmounted) this.setState({ btnLoading: null });
                }
            }, 300);
        });
    };

    handleSearch = () => this.runWithLoader('search', this.handleSearchAction);
    handleShowAll = () => this.runWithLoader('showAll', this.handleShowAllAction);
    handleClear = () => this.runWithLoader('clear', this.handleClearAction);
    handleExportExcel = () => this.runWithLoader('excel', this.handleExportExcelAction);
    handleExportPdf = () => this.runWithLoader('pdf', this.handleExportPdfAction);

    handleSearchAction = () => {
        const { fromDate, toDate, Restaurant } = this.state;
        if (!Restaurant) {
            this.showError('Please select Restaurant');
            return;
        }
        if (!fromDate || !toDate) {
            this.showError('Please select From Date and To Date');
            return;
        }
        if (fromDate > toDate) {
            this.showError('From Date cannot be after To Date');
            return;
        }
        this.setState({ tableData: buildRows(generateDateRange(fromDate, toDate)), searched: true });
    };

    handleShowAllAction = () => {
        const range = defaultRange();
        this.setState({ ...range, tableData: buildRows(generateDateRange(range.fromDate, range.toDate)), searched: true });
    };

    handleClearAction = () => {
        this.setState({ Restaurant: RESTAURANTS[0].value, ...defaultRange(), tableData: [], searched: false });
    };

    getTotals = () => {
        const { tableData } = this.state;
        const sum = (k) => round2(tableData.reduce((a, r) => a + Number(r[k] || 0), 0));
        return { totalSales: sum('totalSales'), gpay: sum('gpay'), cancelled: sum('cancelled'), cash: sum('cash') };
    };

    reportTitle = () => {
        const { fromDate, toDate } = this.state;
        return `DAILY SALES REPORT (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    };

    buildExportMatrix = () => {
        const t = this.getTotals();
        return {
            body: this.state.tableData.map(r => [formatDate(r.date), r.totalSales, r.billFrom, r.billTo, r.gpay, r.cancelled || '', r.cash]),
            total: ['TOTAL', t.totalSales, '', '', t.gpay, t.cancelled || '', t.cash],
        };
    };

    handleExportExcelAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.showError('No data to export');
            return;
        }
        const ws = XLSX.utils.aoa_to_sheet([[`${String(this.state.Restaurant).toUpperCase()} - ${this.reportTitle()}`], COLUMN_LABELS, ...body, total]);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMN_LABELS.length - 1 } }];
        ws['!cols'] = COLUMN_LABELS.map((l, i) => ({ wch: i === 5 ? 24 : 16 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sales Report');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Sales_Report.xlsx');
    };

    handleExportPdfAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.showError('No data to export');
            return;
        }
        const numCols = [1, 4, 5, 6];
        const fmt = (row) => row.map((v, i) => (numCols.includes(i) && v !== '' ? formatNum(v) : v));
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(12);
        doc.text(`${String(this.state.Restaurant).toUpperCase()} - ${this.reportTitle()}`, 14, 10);
        doc.autoTable({
            head: [COLUMN_LABELS],
            body: [...body.map(fmt), fmt(total)],
            startY: 15,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 1.5, lineColor: 0, lineWidth: 0.2, textColor: 0, halign: 'right' },
            columnStyles: { 0: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' } },
            headStyles: { fillColor: [244, 176, 176], textColor: 0, halign: 'center' },
            didParseCell: (d) => {
                if (d.section === 'body' && d.row.index === body.length) d.cell.styles.fontStyle = 'bold';
            },
        });
        doc.save('Sales_Report.pdf');
    };

    numTemplate = (field) => (row) => formatNum(row[field]);

    // Cancelled/Dis/Adv Bill stays blank when zero, as in the reference sheet
    optionalNumTemplate = (field) => (row) => (row[field] ? formatNum(row[field]) : '');

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, fromDate, toDate, searched, Restaurant, btnLoading } = this.state;
        const busy = btnLoading !== null;
        const t = searched ? this.getTotals() : {};
        const center = { textAlign: 'center' };
        const right = { textAlign: 'right' };

        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column header={`${String(Restaurant).toUpperCase()} - ${this.reportTitle()}`} colSpan={7} style={{ textAlign: 'center', background: '#f4b0b0', color: '#000' }} />
                </Row>
                <Row>
                    <Column header="DATE" rowSpan={2} style={{ minWidth: '110px', ...center }} />
                    <Column header="TOTAL SALES" rowSpan={2} style={{ minWidth: '130px', ...center }} />
                    <Column header="BILL NO" colSpan={2} style={center} />
                    <Column header="GPAY" rowSpan={2} style={{ minWidth: '120px', ...center }} />
                    <Column header="CANCELLED/DIS/ADV BILL" rowSpan={2} style={{ minWidth: '170px', ...center }} />
                    <Column header="CASH" rowSpan={2} style={{ minWidth: '120px', ...center }} />
                </Row>
                <Row>
                    <Column header="FROM" style={{ minWidth: '80px', ...center }} />
                    <Column header="TO" style={{ minWidth: '80px', ...center }} />
                </Row>
            </ColumnGroup>
        );

        const footerGroup = (
            <ColumnGroup>
                <Row>
                    <Column footer="TOTAL" footerStyle={center} />
                    <Column footer={formatNum(t.totalSales || 0)} footerStyle={right} />
                    <Column footer="" colSpan={2} />
                    <Column footer={formatNum(t.gpay || 0)} footerStyle={right} />
                    <Column footer={t.cancelled ? formatNum(t.cancelled) : ''} footerStyle={right} />
                    <Column footer={formatNum(t.cash || 0)} footerStyle={right} />
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <SalesReportHeader />

                        <div className="p-grid p-align-end">
                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={RESTAURANTS}
                                    value={Restaurant}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar placeholder="From Date" value={fromDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ fromDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar placeholder="To Date" value={toDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ toDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-5 p-lg-5 p-d-flex p-ai-end p-gap-2">
                                <Button label="Search" icon={btnLoading === 'search' ? 'pi pi-spin pi-spinner' : 'pi pi-search'} className="p-button-primary" onClick={this.handleSearch} disabled={busy} />
                                <Button label="Show All" icon={btnLoading === 'showAll' ? 'pi pi-spin pi-spinner' : 'pi pi-list'} className="p-button-secondary p-ml-2" onClick={this.handleShowAll} disabled={busy} />
                                <Button label="Clear" icon={btnLoading === 'clear' ? 'pi pi-spin pi-spinner' : 'pi pi-times'} className="p-button-secondary p-ml-2" onClick={this.handleClear} disabled={busy} />
                            </div>

                            <div className="p-col-12 p-d-flex p-jc-end p-gap-2 p-mt-1">
                                <Button label="Export as Excel" icon={btnLoading === 'excel' ? 'pi pi-spin pi-spinner' : 'pi pi-file-excel'} className="p-button-warning" onClick={this.handleExportExcel} disabled={!searched || busy} />
                                <Button label="Export as PDF" icon={btnLoading === 'pdf' ? 'pi pi-spin pi-spinner' : 'pi pi-file-pdf'} className="p-button-danger p-ml-2" onClick={this.handleExportPdf} disabled={!searched || busy} />
                            </div>
                        </div>

                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    footerColumnGroup={footerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    style={{ width: '100%' }}
                                    emptyMessage="No records found"
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column field="date" body={(r) => formatDate(r.date)} style={{ minWidth: '110px', ...center }} />
                                    <Column field="totalSales" body={this.numTemplate('totalSales')} style={{ minWidth: '130px', ...right }} />
                                    <Column field="billFrom" style={{ minWidth: '80px', ...center }} />
                                    <Column field="billTo" style={{ minWidth: '80px', ...center }} />
                                    <Column field="gpay" body={this.numTemplate('gpay')} style={{ minWidth: '120px', ...right }} />
                                    <Column field="cancelled" body={this.optionalNumTemplate('cancelled')} style={{ minWidth: '170px', ...right }} />
                                    <Column field="cash" body={this.numTemplate('cash')} style={{ minWidth: '120px', ...right }} />
                                </DataTable>
                            </div>
                        )}

                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(SalesReport);
